import { type CabinAdvancedConfig } from '@/mocks/adminSettingsCabina';

interface Props {
  config: CabinAdvancedConfig;
  onChange: (config: CabinAdvancedConfig) => void;
  onSave: () => void;
  saving: boolean;
}

export default function CabinConfigSection({ config, onChange, onSave, saving }: Props) {
  const toggle = (key: keyof CabinAdvancedConfig) => {
    onChange({ ...config, [key]: !config[key] });
  };

  const configItems = [
    {
      key: 'activarCabina' as const,
      label: 'Activar Cabina',
      description: 'Habilita el chat en vivo para que los oyentes interactúen durante la transmisión.',
      icon: 'ri-broadcast-line',
      color: 'bg-emerald-500',
    },
    {
      key: 'mensajesVoz' as const,
      label: 'Mensajes de Voz',
      description: 'Permite que los oyentes envíen notas de voz que se reproducen al aire.',
      icon: 'ri-mic-line',
      color: 'bg-orange-500',
    },
    {
      key: 'solicitudes' as const,
      label: 'Solicitudes',
      description: 'Los oyentes pueden pedir canciones y los locutores las ven en tiempo real.',
      icon: 'ri-music-line',
      color: 'bg-violet-500',
    },
    {
      key: 'concursos' as const,
      label: 'Concursos',
      description: 'Activa dinámicas de trivia, votaciones y sorteos directamente en la cabina.',
      icon: 'ri-trophy-line',
      color: 'bg-amber-500',
    },
  ];

  return (
    <div className="space-y-5">
      <div className="space-y-3">
        {configItems.map((item) => (
          <div
            key={item.key}
            className={`flex items-start gap-4 p-4 rounded-xl border transition-all ${
              config[item.key]
                ? 'border-secondary-200 bg-secondary-50/50'
                : 'border-background-200 bg-background-50'
            }`}
          >
            <div
              className={`w-10 h-10 rounded-xl ${item.color} flex items-center justify-center shrink-0 mt-0.5 ${
                !config[item.key] && 'opacity-30'
              }`}
            >
              <i className={`${item.icon} text-background-50 text-base`}></i>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold text-foreground-900">{item.label}</h4>
                <button
                  onClick={() => toggle(item.key)}
                  className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer shrink-0 ${
                    config[item.key] ? 'bg-secondary-500' : 'bg-background-300'
                  }`}
                >
                  <span
                    className={`absolute top-0.5 w-5 h-5 rounded-full bg-background-50 transition-all ${
                      config[item.key] ? 'left-[22px]' : 'left-0.5'
                    }`}
                  ></span>
                </button>
              </div>
              <p className="text-foreground-500 text-[10px] leading-relaxed mt-1">{item.description}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Info box */}
      <div className="p-4 rounded-xl bg-accent-50 border border-accent-200">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center shrink-0">
            <i className="ri-information-line text-accent-500 text-sm"></i>
          </div>
          <div>
            <p className="text-accent-700 text-[11px] font-semibold mb-0.5">Configuración de Cabina</p>
            <p className="text-accent-600/70 text-[10px] leading-relaxed">
              Estas opciones controlan las funcionalidades disponibles en la cabina de tu radio. Los cambios se aplican al instante para todos los oyentes conectados.
            </p>
          </div>
        </div>
      </div>

      {/* Save */}
      <div className="flex items-center justify-between pt-2 border-t border-background-200">
        <p className="text-foreground-400 text-[10px]">
          {config.activarCabina ? 'Cabina activa' : 'Cabina desactivada'}
          {' · '}
          {[config.mensajesVoz, config.solicitudes, config.concursos].filter(Boolean).length} funciones extra activas
        </p>
        <button
          onClick={onSave}
          disabled={saving}
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-foreground-950 text-background-50 text-xs font-semibold hover:bg-foreground-800 transition-all cursor-pointer disabled:opacity-60 whitespace-nowrap"
        >
          {saving ? (
            <>
              <i className="ri-loader-4-line animate-spin text-sm"></i>
              Guardando...
            </>
          ) : (
            <>
              <i className="ri-check-line text-sm"></i>
              Guardar Cambios
            </>
          )}
        </button>
      </div>
    </div>
  );
}